import { useParams } from "react-router";
import type {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useDQuery } from "src/hooks/useDQuery";
import { buildQueryUrl, type SimpleUrlBuilder } from "src/queries/buildQueryUrl";
import {
  type CommonParams,
  mergeBodyWithDefaults,
  useCommonQueryParams,
} from "src/queries/useCommonQueryParams";
import type { FetchParams } from "src/utils/dfetch.ts";
import type { PartialBy } from "src/utils/ts-utils";

/** Enabled predicate: (p) => truthy/falsy value */
type EnabledPredicate = (params: CommonParams) => unknown;

/** Computed param predicate: (p) => computed value */
type ComputedParamPredicate = (params: CommonParams) => unknown;

/** Computed param: { paramName: (p) => value } */
type ComputedParam = Record<string, ComputedParamPredicate>;

/** Query param item: either a key of CommonParams or a computed param object */
type QueryParamItem = keyof CommonParams | ComputedParam;

/** Native select function type */
type NativeSelectFn<Response, Transformed> = (data: Response) => Transformed;

/** Hook properties attached to the returned function */
type HookQueryProps<Response, Transformed> = {
  url: SimpleUrlBuilder | string;
  select: NativeSelectFn<Response, Transformed>;
};

/** Hook function returned by makeQuery */
type QueryHookFn<Response, Transformed = Response> = (<Select = Transformed>(
  queryOpts?: Partial<MakeQueryInternalProps<Response, Select>>
) => UseQueryResult<Select, Error>) &
  HookQueryProps<Response, Transformed>;

type MakeQueryOptions<Response, Transformed> = {
  queryParams?: QueryParamItem[];
  enabled?: EnabledPredicate;
  /** Native select applied by default. Exposed via hook.select for composition. */
  select?: NativeSelectFn<Response, Transformed>;
};

type MakeQueryInternalProps<T, Select> = Omit<FetchParams, "url"> &
  PartialBy<UseQueryOptions<T, Error, Select, QueryKey>, "queryKey" | "queryFn"> & {
    defaultParamsList?: (keyof CommonParams)[];
    enabledPredicate?: EnabledPredicate;
    nativeSelect?: NativeSelectFn<T, Select>;
    params?: Partial<{ [K in keyof CommonParams]: CommonParams[K] | null }> &
      Record<string, unknown>;
    url?: SimpleUrlBuilder | string;
  };

export function makeQuery<Response, Transformed = Response>(
  url: SimpleUrlBuilder | string,
  options?: MakeQueryOptions<Response, Transformed>
): QueryHookFn<Response, Transformed> {
  const nativeSelect =
    options?.select ?? ((d: Response) => d as unknown as Transformed);

  const hook = (<Select = Transformed>(
    queryOpts?: Partial<MakeQueryInternalProps<Response, Select>>
  ) => {
    const { params, ...restProps } = queryOpts ?? {};

    const queryParamsConfig = options?.queryParams ?? [];
    const enabledPredicate = options?.enabled;
    const nativeSelectForCall = queryOpts?.select
      ? undefined
      : (nativeSelect as unknown as NativeSelectFn<Response, Select>);

    // Separate string keys from computed param objects
    const defaultParamsList = queryParamsConfig.filter(
      (item): item is keyof CommonParams => typeof item === "string"
    );
    const computedParams = queryParamsConfig.filter(
      (item): item is ComputedParam => typeof item === "object"
    );

    const nativeParams = useParams();
    const allParamsForUrl = useCommonQueryParams();
    const mergedParamsForUrl = mergeBodyWithDefaults(
      {
        ...allParamsForUrl,
        ...(nativeParams.cluster_name && {
          cluster_name: nativeParams.cluster_name,
        }),
      },
      params
    );

    // defaultParamsList comes from the queryParams of the hook definition - example:
    //    const useQueryTasks = makeQuery(..., { queryParams })
    const paramsForQuery = useCommonQueryParams(defaultParamsList);

    // Evaluate computed params using the full merged params
    const evaluatedComputedParams = Object.fromEntries(
      computedParams.flatMap((obj) =>
        Object.entries(obj).map(([key, predicate]) => [
          key,
          predicate(mergedParamsForUrl),
        ])
      )
    );

    const mergedQueryParams = mergeBodyWithDefaults(
      { ...paramsForQuery, ...evaluatedComputedParams },
      params
    );

    const {
      url: resolvedUrl,
      isUrlValid,
      urlParams,
    } = typeof url === "function"
      ? buildQueryUrl(url, mergedParamsForUrl)
      : { url, isUrlValid: true, urlParams: [] as string[] };

    const bodyWithoutUrlParams = Object.fromEntries(
      Object.entries(mergedQueryParams).filter(([key]) => !urlParams.includes(key))
    );

    const enabled =
      // `enabledPredicate` comes from hook definition - example: const useQueryTasks = makeQuery(...)
      // `restProps.enabled` comes from hook call - example: useQueryTasks({ enabled: true })
      (typeof restProps.enabled !== "undefined"
        ? !!restProps.enabled
        : enabledPredicate
          ? !!enabledPredicate(mergedParamsForUrl)
          : true) && isUrlValid;

    // Use nativeSelect if provided and user hasn't passed their own select
    const select = restProps.select ?? nativeSelectForCall;

    return useDQuery<Response, Select>({
      ...restProps,
      enabled,
      select,
      url: resolvedUrl,
      body: bodyWithoutUrlParams,
    });
  }) as QueryHookFn<Response, Transformed>;

  hook.url = url;
  hook.select = nativeSelect;

  return hook;
}
